import React from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  StepConnector,
  Box,
  LinearProgress,
  Typography,
  useTheme,
  alpha,
} from '@mui/material';
import { stepConnectorClasses } from '@mui/material/StepConnector';
import { styled } from '@mui/material/styles';
import { CheckCircle } from '@mui/icons-material';
import { STEP_LABELS, TOTAL_STEPS, getStatusConfig } from '../../utils/transformationHelpers';

interface StatusStepperProps {
  status: string;
  compact?: boolean;
}

const CompactConnector = styled(StepConnector)(({ theme }) => ({
  [`&.${stepConnectorClasses.alternativeLabel}`]: {
    top: 10,
    left: 'calc(-50% + 10px)',
    right: 'calc(50% + 10px)',
  },
  [`& .${stepConnectorClasses.line}`]: {
    height: 2,
    border: 0,
    backgroundColor: theme.palette.divider,
    borderRadius: 1,
  },
  [`&.${stepConnectorClasses.active} .${stepConnectorClasses.line}`]: {
    backgroundColor: theme.palette.primary.main,
  },
  [`&.${stepConnectorClasses.completed} .${stepConnectorClasses.line}`]: {
    backgroundColor: theme.palette.success.main,
  },
}));

const StatusStepper: React.FC<StatusStepperProps> = ({ status, compact = false }) => {
  const theme = useTheme();
  const config = getStatusConfig(status);
  const activeStep = config.step;
  const isFailed = activeStep < 0;
  const progressPercent = isFailed ? 0 : Math.round((activeStep / (TOTAL_STEPS - 1)) * 100);

  if (compact) {
    return (
      <Box sx={{ width: '100%' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
          <Typography variant="caption" fontWeight={600} color={`${config.color}.main`}>
            {config.label}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {isFailed ? '' : `Step ${activeStep + 1} of ${TOTAL_STEPS}`}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={isFailed ? 100 : progressPercent}
          color={isFailed ? 'error' : activeStep >= TOTAL_STEPS - 1 ? 'success' : 'primary'}
          sx={{ height: 6, borderRadius: 3 }}
        />
      </Box>
    );
  }

  return (
    <Stepper
      activeStep={isFailed ? -1 : activeStep}
      alternativeLabel
      connector={<CompactConnector />}
      sx={{ py: 1 }}
    >
      {STEP_LABELS.map((label, index) => {
        const completed = !isFailed && index < activeStep;
        const active = !isFailed && index === activeStep;
        return (
          <Step key={label} completed={completed} active={active}>
            <StepLabel
              StepIconComponent={() => (
                <Box
                  sx={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: completed
                      ? 'success.main'
                      : active
                        ? 'primary.main'
                        : alpha(theme.palette.text.disabled, 0.2),
                    color: completed || active ? 'white' : 'text.disabled',
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  {completed ? (
                    <CheckCircle sx={{ fontSize: 14 }} />
                  ) : (
                    index + 1
                  )}
                </Box>
              )}
              sx={{
                '& .MuiStepLabel-label': {
                  fontSize: '0.65rem',
                  mt: 0.5,
                  color: completed
                    ? 'success.main'
                    : active
                      ? 'primary.main'
                      : 'text.disabled',
                  fontWeight: active ? 600 : 400,
                },
              }}
            >
              {label}
            </StepLabel>
          </Step>
        );
      })}
    </Stepper>
  );
};

export default StatusStepper;
